import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2 as Grid,
  Card,
  CardContent,
  Tab,
  Tabs,
  Alert,
  Tooltip,
  Badge,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Search,
  Add,
  Remove,
  Inventory2,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  SwapVert,
  Refresh,
  Settings,
  Visibility,
  Done,
  History,
} from '@mui/icons-material';
import inventoryService, {
  InventoryProduct,
  StockMovement,
  StockAlert,
  InventoryOverview,
  AlertCounts,
  MovementType,
  StockStatus,
} from '../../services/inventory.service';
import shopService from '../../services/shop.service';
import { useTranslation } from 'react-i18next';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const Inventory: React.FC = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  // Overview data
  const [overview, setOverview] = useState<InventoryOverview | null>(null);
  const [alertCounts, setAlertCounts] = useState<AlertCounts | null>(null);

  // Products
  const [products, setProducts] = useState<InventoryProduct[]>([]);
  const [productPage, setProductPage] = useState(0);
  const [productRowsPerPage, setProductRowsPerPage] = useState(25);
  const [productTotal, setProductTotal] = useState(0);
  const [productSearch, setProductSearch] = useState('');
  const [productStatusFilter, setProductStatusFilter] = useState<StockStatus | 'all'>('all');

  // Movements
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [movementPage, setMovementPage] = useState(0);
  const [movementRowsPerPage, setMovementRowsPerPage] = useState(25);
  const [movementTotal, setMovementTotal] = useState(0);
  const [movementTypeFilter, setMovementTypeFilter] = useState<MovementType | 'all'>('all');

  // Alerts
  const [alerts, setAlerts] = useState<StockAlert[]>([]);
  const [alertPage, setAlertPage] = useState(0);
  const [alertRowsPerPage, setAlertRowsPerPage] = useState(25);
  const [alertTotal, setAlertTotal] = useState(0);
  const [alertResolvedFilter, setAlertResolvedFilter] = useState<'all' | 'pending' | 'resolved'>('pending');

  // Stores filter
  const [stores, setStores] = useState<{ id: string; name: string }[]>([]);
  const [storeFilter, setStoreFilter] = useState<string>('');

  // Movement dialog
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<InventoryProduct | null>(null);
  const [movementType, setMovementType] = useState<MovementType>('entrada');
  const [movementQuantity, setMovementQuantity] = useState<number>(0);
  const [movementReason, setMovementReason] = useState('');
  const [movementNotes, setMovementNotes] = useState('');

  // Settings dialog
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editMinStock, setEditMinStock] = useState<number>(0);
  const [editTrackStock, setEditTrackStock] = useState<boolean>(true);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (tabValue === 0) loadProducts();
    else if (tabValue === 1) loadMovements();
    else if (tabValue === 2) loadAlerts();
  }, [tabValue, productPage, productRowsPerPage, productSearch, productStatusFilter, storeFilter]);

  useEffect(() => {
    if (tabValue === 1) loadMovements();
  }, [movementPage, movementRowsPerPage, movementTypeFilter]);

  useEffect(() => {
    if (tabValue === 2) loadAlerts();
  }, [alertPage, alertRowsPerPage, alertResolvedFilter]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load stores
      const storesRes = await shopService.getStores({ limit: 100 });
      setStores(storesRes.data.map((s: any) => ({ id: s.id, name: s.name })));

      // Load overview
      await loadOverview();

      // Load alert counts
      await loadAlertCounts();

      // Load products
      await loadProducts();
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOverview = async () => {
    try {
      const res = await inventoryService.getOverview(storeFilter ? Number(storeFilter) : undefined);
      setOverview(res.data);
      if (res.needsSetup) setNeedsSetup(true);
    } catch (error) {
      console.error('Error loading overview:', error);
    }
  };

  const loadAlertCounts = async () => {
    try {
      const res = await inventoryService.getAlertCounts(storeFilter ? Number(storeFilter) : undefined);
      setAlertCounts(res.data);
    } catch (error) {
      console.error('Error loading alert counts:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const res = await inventoryService.getProducts({
        page: productPage + 1,
        limit: productRowsPerPage,
        storeId: storeFilter ? Number(storeFilter) : undefined,
        status: productStatusFilter !== 'all' ? productStatusFilter : undefined,
        search: productSearch || undefined,
      });
      setProducts(res.data);
      setProductTotal(res.pagination.total);
      if (res.needsSetup) setNeedsSetup(true);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadMovements = async () => {
    try {
      const res = await inventoryService.getMovements({
        page: movementPage + 1,
        limit: movementRowsPerPage,
        storeId: storeFilter ? Number(storeFilter) : undefined,
        type: movementTypeFilter !== 'all' ? movementTypeFilter : undefined,
      });
      setMovements(res.data);
      setMovementTotal(res.pagination.total);
    } catch (error) {
      console.error('Error loading movements:', error);
    }
  };

  const loadAlerts = async () => {
    try {
      const res = await inventoryService.getAlerts({
        page: alertPage + 1,
        limit: alertRowsPerPage,
        storeId: storeFilter ? Number(storeFilter) : undefined,
        resolved: alertResolvedFilter === 'all' ? undefined : alertResolvedFilter === 'resolved',
      });
      setAlerts(res.data);
      setAlertTotal(res.pagination.total);
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  const handleSetup = async () => {
    try {
      await inventoryService.setupTables();
      setNeedsSetup(false);
      setSnackbar({ open: true, message: t('shop.inventorySetupSuccess'), severity: 'success' });
      loadInitialData();
    } catch (error) {
      setSnackbar({ open: true, message: t('shop.errorSetupInventory'), severity: 'error' });
    }
  };

  const handleOpenMovementDialog = (product: InventoryProduct, type: MovementType) => {
    setSelectedProduct(product);
    setMovementType(type);
    setMovementQuantity(0);
    setMovementReason('');
    setMovementNotes('');
    setMovementDialogOpen(true);
  };

  const handleCreateMovement = async () => {
    if (!selectedProduct || movementQuantity <= 0) return;

    try {
      await inventoryService.createMovement({
        productId: selectedProduct.id as any,
        storeId: selectedProduct.storeId ? selectedProduct.storeId as any : undefined,
        type: movementType,
        quantity: movementQuantity,
        reason: movementReason,
        notes: movementNotes,
      });
      setMovementDialogOpen(false);
      setSnackbar({ open: true, message: t('shop.movementSuccess'), severity: 'success' });
      loadProducts();
      loadOverview();
      loadAlertCounts();
    } catch (error) {
      setSnackbar({ open: true, message: t('shop.movementError'), severity: 'error' });
    }
  };

  const handleOpenSettingsDialog = (product: InventoryProduct) => {
    setSelectedProduct(product);
    setEditMinStock(product.minStock);
    setEditTrackStock(product.trackStock);
    setSettingsDialogOpen(true);
  };

  const handleUpdateSettings = async () => {
    if (!selectedProduct) return;

    try {
      await inventoryService.updateProductStock(selectedProduct.id, {
        minStock: editMinStock,
        trackStock: editTrackStock,
      });
      setSettingsDialogOpen(false);
      setSnackbar({ open: true, message: t('shop.settingsUpdated'), severity: 'success' });
      loadProducts();
    } catch (error) {
      setSnackbar({ open: true, message: t('shop.settingsUpdateError'), severity: 'error' });
    }
  };

  const handleResolveAlert = async (alertId: string) => {
    try {
      await inventoryService.resolveAlert(Number(alertId));
      setSnackbar({ open: true, message: t('shop.alertResolvedSuccess'), severity: 'success' });
      loadAlerts();
      loadAlertCounts();
    } catch (error) {
      setSnackbar({ open: true, message: t('shop.alertResolveError'), severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('shop.inventoryTitle')}</Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('shop.storeLabel')}</InputLabel>
            <Select
              value={storeFilter}
              label={t('shop.storeLabel')}
              onChange={(e) => setStoreFilter(e.target.value)}
            >
              <MenuItem value="">{t('shop.allStoresOption')}</MenuItem>
              {stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>{store.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button startIcon={<Refresh />} onClick={loadInitialData}>
            {t('shop.update')}
          </Button>
        </Box>
      </Box>

      {needsSetup && (
        <Alert severity="info" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={handleSetup}>
            {t('shop.setupInventory')}
          </Button>
        }>
          {t('shop.setupInventoryMsg')}
        </Alert>
      )}

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    {t('shop.totalProductsStat')}
                  </Typography>
                  <Typography variant="h4">{overview?.totalProducts || 0}</Typography>
                </Box>
                <Inventory2 sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    {t('shop.inStock')}
                  </Typography>
                  <Typography variant="h4" color="success.main">{overview?.inStock || 0}</Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    {t('shop.lowStock')}
                  </Typography>
                  <Typography variant="h4" color="warning.main">{overview?.lowStock || 0}</Typography>
                </Box>
                <Warning sx={{ fontSize: 40, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    {t('shop.outOfStock')}
                  </Typography>
                  <Typography variant="h4" color="error.main">{overview?.outOfStock || 0}</Typography>
                </Box>
                <ErrorIcon sx={{ fontSize: 40, color: 'error.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label={t('shop.productsTab')} icon={<Inventory2 />} iconPosition="start" />
          <Tab label={t('shop.movementsTab')} icon={<History />} iconPosition="start" />
          <Tab
            label={
              <Badge badgeContent={alertCounts?.total || 0} color="error">
                <Box sx={{ pr: alertCounts?.total ? 2 : 0 }}>{t('shop.alertsTab')}</Box>
              </Badge>
            }
            icon={<Warning />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Products Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper>
          <Box p={2} display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder={t('shop.searchProduct')}
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('shop.statusFilterLabel')}</InputLabel>
              <Select
                value={productStatusFilter}
                label={t('shop.statusFilterLabel')}
                onChange={(e) => setProductStatusFilter(e.target.value as StockStatus | 'all')}
              >
                <MenuItem value="all">{t('shop.allFilter')}</MenuItem>
                <MenuItem value="ok">{t('shop.inStock')}</MenuItem>
                <MenuItem value="low">{t('shop.lowStock')}</MenuItem>
                <MenuItem value="out">{t('shop.outOfStock')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('shop.productCol')}</TableCell>
                  <TableCell>{t('shop.storeCol')}</TableCell>
                  <TableCell align="right">{t('shop.priceHeader')}</TableCell>
                  <TableCell align="center">{t('shop.stockHeader')}</TableCell>
                  <TableCell align="center">{t('shop.minimumHeader')}</TableCell>
                  <TableCell align="center">{t('shop.statusFilterLabel')}</TableCell>
                  <TableCell align="right">{t('shop.stockValueHeader')}</TableCell>
                  <TableCell align="center">{t('shop.actionsCol')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {product.image ? (
                          <img src={product.image} alt={product.name} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                        ) : (
                          <Box sx={{ width: 40, height: 40, bgcolor: 'action.selected', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Inventory2 fontSize="small" color="disabled" />
                          </Box>
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{product.name}</Typography>
                          {product.categoryName && (
                            <Typography variant="caption" color="textSecondary">{product.categoryName}</Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{product.storeName || '-'}</TableCell>
                    <TableCell align="right">{inventoryService.formatCurrency(product.price)}</TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold" color={product.stockStatus === 'out' ? 'error' : product.stockStatus === 'low' ? 'warning.main' : 'success.main'}>
                        {product.stock}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{product.minStock}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={inventoryService.getStockStatusLabel(product.stockStatus)}
                        color={inventoryService.getStockStatusColor(product.stockStatus)}
                      />
                    </TableCell>
                    <TableCell align="right">{inventoryService.formatCurrency(product.stockValue)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('shop.entryTooltip')}>
                        <IconButton size="small" color="success" onClick={() => handleOpenMovementDialog(product, 'entrada')}>
                          <Add />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('shop.exitTooltip')}>
                        <IconButton size="small" color="error" onClick={() => handleOpenMovementDialog(product, 'saida')}>
                          <Remove />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('shop.adjustTooltip')}>
                        <IconButton size="small" color="warning" onClick={() => handleOpenMovementDialog(product, 'ajuste')}>
                          <SwapVert />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('shop.settingsTooltip')}>
                        <IconButton size="small" onClick={() => handleOpenSettingsDialog(product)}>
                          <Settings />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {products.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary" py={4}>
                        {t('shop.noProductsFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={productTotal}
            page={productPage}
            onPageChange={(_, p) => setProductPage(p)}
            rowsPerPage={productRowsPerPage}
            onRowsPerPageChange={(e) => {
              setProductRowsPerPage(parseInt(e.target.value, 10));
              setProductPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t('shop.rowsPerPage')}
          />
        </Paper>
      </TabPanel>

      {/* Movements Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper>
          <Box p={2} display="flex" gap={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('shop.typeFilter')}</InputLabel>
              <Select
                value={movementTypeFilter}
                label={t('shop.typeFilter')}
                onChange={(e) => setMovementTypeFilter(e.target.value as MovementType | 'all')}
              >
                <MenuItem value="all">{t('shop.allFilter')}</MenuItem>
                <MenuItem value="entrada">{t('shop.entryType')}</MenuItem>
                <MenuItem value="saida">{t('shop.exitType')}</MenuItem>
                <MenuItem value="ajuste">{t('shop.adjustType')}</MenuItem>
                <MenuItem value="venda">{t('shop.saleType')}</MenuItem>
                <MenuItem value="devolucao">{t('shop.returnType')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('shop.dateCol')}</TableCell>
                  <TableCell>{t('shop.productCol')}</TableCell>
                  <TableCell>{t('shop.storeCol')}</TableCell>
                  <TableCell align="center">{t('shop.typeCol')}</TableCell>
                  <TableCell align="center">{t('shop.quantityCol')}</TableCell>
                  <TableCell align="center">{t('shop.previousCol')}</TableCell>
                  <TableCell align="center">{t('shop.newCol')}</TableCell>
                  <TableCell>{t('shop.reasonCol')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.map((movement) => (
                  <TableRow key={movement.id}>
                    <TableCell>{inventoryService.formatDate(movement.date)}</TableCell>
                    <TableCell>{movement.productName}</TableCell>
                    <TableCell>{movement.storeName || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={inventoryService.getMovementTypeLabel(movement.type)}
                        color={inventoryService.getMovementTypeColor(movement.type)}
                        icon={movement.type === 'entrada' || movement.type === 'devolucao' ? <TrendingUp /> : <TrendingDown />}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold" color={movement.type === 'entrada' || movement.type === 'devolucao' ? 'success.main' : 'error.main'}>
                        {movement.type === 'entrada' || movement.type === 'devolucao' ? '+' : '-'}{movement.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{movement.previousQty}</TableCell>
                    <TableCell align="center">{movement.newQty}</TableCell>
                    <TableCell>{movement.reason || '-'}</TableCell>
                  </TableRow>
                ))}
                {movements.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary" py={4}>
                        {t('shop.noMovementsFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={movementTotal}
            page={movementPage}
            onPageChange={(_, p) => setMovementPage(p)}
            rowsPerPage={movementRowsPerPage}
            onRowsPerPageChange={(e) => {
              setMovementRowsPerPage(parseInt(e.target.value, 10));
              setMovementPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t('shop.rowsPerPage')}
          />
        </Paper>
      </TabPanel>

      {/* Alerts Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper>
          <Box p={2} display="flex" gap={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('shop.statusFilterLabel')}</InputLabel>
              <Select
                value={alertResolvedFilter}
                label={t('shop.statusFilterLabel')}
                onChange={(e) => setAlertResolvedFilter(e.target.value as 'all' | 'pending' | 'resolved')}
              >
                <MenuItem value="all">{t('shop.alertStatusAll')}</MenuItem>
                <MenuItem value="pending">{t('shop.alertStatusPending')}</MenuItem>
                <MenuItem value="resolved">{t('shop.alertStatusResolved')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('shop.alertDateCol')}</TableCell>
                  <TableCell>{t('shop.alertProductCol')}</TableCell>
                  <TableCell>{t('shop.alertStoreCol')}</TableCell>
                  <TableCell align="center">{t('shop.alertTypeCol')}</TableCell>
                  <TableCell align="center">{t('shop.alertCurrentQty')}</TableCell>
                  <TableCell align="center">{t('shop.alertMinQty')}</TableCell>
                  <TableCell align="center">{t('shop.alertStatusCol')}</TableCell>
                  <TableCell align="center">{t('shop.alertActionsCol')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alerts.map((alert) => (
                  <TableRow key={alert.id} sx={{ bgcolor: !alert.resolved ? 'error.lighter' : undefined }}>
                    <TableCell>{inventoryService.formatDate(alert.date)}</TableCell>
                    <TableCell>{alert.productName}</TableCell>
                    <TableCell>{alert.storeName || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={inventoryService.getAlertTypeLabel(alert.type)}
                        color={inventoryService.getAlertTypeColor(alert.type)}
                        icon={alert.type === 'sem_estoque' ? <ErrorIcon /> : <Warning />}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold" color={alert.type === 'sem_estoque' ? 'error' : 'warning.main'}>
                        {alert.currentQty}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{alert.minQty}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={alert.resolved ? t('shop.alertResolved') : t('shop.alertPending')}
                        color={alert.resolved ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {!alert.resolved && (
                        <Tooltip title={t('shop.markResolved')}>
                          <IconButton size="small" color="success" onClick={() => handleResolveAlert(alert.id)}>
                            <Done />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {alerts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary" py={4}>
                        {t('shop.noAlertsFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={alertTotal}
            page={alertPage}
            onPageChange={(_, p) => setAlertPage(p)}
            rowsPerPage={alertRowsPerPage}
            onRowsPerPageChange={(e) => {
              setAlertRowsPerPage(parseInt(e.target.value, 10));
              setAlertPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t('shop.rowsPerPage')}
          />
        </Paper>
      </TabPanel>

      {/* Movement Dialog */}
      <Dialog open={movementDialogOpen} onClose={() => setMovementDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {movementType === 'entrada' ? t('shop.stockEntryTitle') :
            movementType === 'saida' ? t('shop.stockExitTitle') : t('shop.stockAdjustTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="textSecondary" mb={2}>
              {t('shop.productLabel')}: <strong>{selectedProduct?.name}</strong>
              <br />
              {t('shop.currentStock')}: <strong>{selectedProduct?.stock}</strong>
            </Typography>

            <TextField
              fullWidth
              type="number"
              label={movementType === 'ajuste' ? t('shop.newQuantityLabel') : t('shop.quantityLabel')}
              value={movementQuantity}
              onChange={(e) => setMovementQuantity(Number(e.target.value))}
              inputProps={{ min: 0 }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('shop.reasonLabel')}
              value={movementReason}
              onChange={(e) => setMovementReason(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('shop.notesLabel')}
              value={movementNotes}
              onChange={(e) => setMovementNotes(e.target.value)}
              multiline
              rows={2}
            />

            {movementType !== 'ajuste' && movementQuantity > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('shop.newStockInfo')}: {movementType === 'entrada'
                  ? (selectedProduct?.stock || 0) + movementQuantity
                  : Math.max(0, (selectedProduct?.stock || 0) - movementQuantity)}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMovementDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleCreateMovement}
            disabled={movementQuantity <= 0}
            color={movementType === 'entrada' ? 'success' : movementType === 'saida' ? 'error' : 'warning'}
          >
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('shop.stockSettingsTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="textSecondary" mb={2}>
              {t('shop.productLabel')}: <strong>{selectedProduct?.name}</strong>
            </Typography>

            <TextField
              fullWidth
              type="number"
              label={t('shop.minStockLabel')}
              value={editMinStock}
              onChange={(e) => setEditMinStock(Number(e.target.value))}
              inputProps={{ min: 0 }}
              helperText={t('shop.minStockHelp')}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth>
              <InputLabel>{t('shop.trackStockLabel')}</InputLabel>
              <Select
                value={editTrackStock ? 'sim' : 'nao'}
                label={t('shop.trackStockLabel')}
                onChange={(e) => setEditTrackStock(e.target.value === 'sim')}
              >
                <MenuItem value="sim">{t('common.yes')}</MenuItem>
                <MenuItem value="nao">{t('common.no')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleUpdateSettings}>
            {t('shop.saveButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Inventory;
